import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { listCacheFallback } from '@/lib/list-cache-fallback';

const nowIso = () => new Date().toISOString();
const OFFLINE_PREFIX = 'OFFLINE-';
import type {
  StorefrontCategory,
  CreateStorefrontCategoryInput,
  UpdateStorefrontCategoryInput,
  StorefrontCategoryFilters,
  StorefrontProduct,
  CreateStorefrontProductInput,
  UpdateStorefrontProductInput,
  StorefrontProductFilters,
  OnlineCustomer,
  CreateOnlineCustomerInput,
  UpdateOnlineCustomerInput,
  OnlineCustomerFilters,
  StoreSettings,
  UpdateStoreSettingsInput,
  StoreTheme,
  UpdateStoreThemeInput,
  StoreBanner,
  CreateStoreBannerInput,
  UpdateStoreBannerInput,
  StorePage,
  UpdateStorePageInput,
  StoreCheckoutField,
  CreateStoreCheckoutFieldInput,
  UpdateStoreCheckoutFieldInput,
  Shipment,
  CreateShipmentInput,
  UpdateShipmentInput,
  StorefrontAnalyticsRange,
  NotificationKind,
} from '@erp/data-access/interfaces';

// ---- Categories ----

const CAT_KEY = ['storefront-categories'] as const;

export function useStorefrontCategories(filters?: StorefrontCategoryFilters) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...CAT_KEY, filters],
    queryFn: () => ds.storefrontCategories.list(filters),
  });
}

export function useStorefrontCategory(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...CAT_KEY, id],
    queryFn: () => ds.storefrontCategories.getById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<StorefrontCategory>(qc, CAT_KEY, id),
  });
}

function readCachedStorefrontCategory(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
): Partial<StorefrontCategory> {
  const direct = qc.getQueryData<StorefrontCategory>([...CAT_KEY, id]);
  if (direct) return direct;
  for (const [, list] of qc.getQueriesData<StorefrontCategory[]>({ queryKey: CAT_KEY })) {
    if (Array.isArray(list)) {
      const found = list.find((c) => c.id === id);
      if (found) return found;
    }
  }
  return { id };
}

export function useCreateStorefrontCategory() {
  const ds = useDataService();
  return useResilientMutation<CreateStorefrontCategoryInput, StorefrontCategory>({
    entity: 'storefront_categories',
    op: 'create',
    invalidate: [CAT_KEY],
    online: (input) => ds.storefrontCategories.create(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        articleCategoryId: input.articleCategoryId,
        slug: input.slug ?? OFFLINE_PREFIX + clientId.slice(0, 6).toLowerCase(),
        nameFr: input.nameFr ?? null,
        nameAr: input.nameAr ?? null,
        nameEn: input.nameEn ?? null,
        bannerImageUrl: input.bannerImageUrl ?? null,
        displayOrder: input.displayOrder ?? 0,
        isPublished: input.isPublished ?? false,
        adsLinkSlug: input.adsLinkSlug ?? null,
        createdAt: nowIso(),
        updatedAt: nowIso(),
      }) as StorefrontCategory,
    applyOptimistic: (qc, newCat) => {
      qc.setQueriesData<StorefrontCategory[]>({ queryKey: CAT_KEY }, (old) => {
        if (!Array.isArray(old)) return old;
        if (old.some((c) => c.id === newCat.id)) return old;
        return [...old, newCat].sort((a, b) => a.displayOrder - b.displayOrder);
      });
    },
  });
}

export function useUpdateStorefrontCategory() {
  const qc = useQueryClient();
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateStorefrontCategoryInput }, StorefrontCategory>({
    entity: 'storefront_categories',
    op: 'update',
    invalidate: [CAT_KEY],
    online: ({ id, input }) => ds.storefrontCategories.update(id, input),
    optimistic: ({ id, input }) => {
      const cached = readCachedStorefrontCategory(qc, id);
      return { ...cached, ...input, id, updatedAt: nowIso() } as StorefrontCategory;
    },
    applyOptimistic: (qc, updated, { id }) => {
      qc.setQueriesData<StorefrontCategory[]>({ queryKey: CAT_KEY }, (old) =>
        Array.isArray(old) ? old.map((c) => (c.id === id ? updated : c)) : old,
      );
      qc.setQueryData<StorefrontCategory>([...CAT_KEY, id], updated);
    },
  });
}

export function useDeleteStorefrontCategory() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'storefront_categories',
    op: 'delete',
    invalidate: [CAT_KEY],
    online: (id) => ds.storefrontCategories.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _result, id) => {
      qc.setQueriesData<StorefrontCategory[]>({ queryKey: CAT_KEY }, (old) =>
        Array.isArray(old) ? old.filter((c) => c.id !== id) : old,
      );
      qc.removeQueries({ queryKey: [...CAT_KEY, id] });
    },
  });
}

export function useReorderStorefrontCategories() {
  const ds = useDataService();
  return useResilientMutation<string[], void>({
    entity: 'storefront_categories',
    op: 'custom',
    method: 'reorder',
    invalidate: [CAT_KEY],
    online: (ids) => ds.storefrontCategories.reorder(ids),
    optimistic: () => undefined as unknown as void,
    // Best-effort: reorder the in-memory list to match the new order so
    // the drag-and-drop result sticks even offline. The server-confirmed
    // order replaces this on reconnect via the invalidate.
    applyOptimistic: (qc, _result, ids) => {
      qc.setQueriesData<StorefrontCategory[]>({ queryKey: CAT_KEY }, (old) => {
        if (!Array.isArray(old)) return old;
        const indexById = new Map(ids.map((id, i) => [id, i]));
        return [...old].sort((a, b) => {
          const ai = indexById.get(a.id) ?? Number.MAX_SAFE_INTEGER;
          const bi = indexById.get(b.id) ?? Number.MAX_SAFE_INTEGER;
          return ai - bi;
        });
      });
    },
  });
}

// ---- Products ----

const PROD_KEY = ['storefront-products'] as const;

export function useStorefrontProducts(filters?: StorefrontProductFilters) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...PROD_KEY, filters],
    queryFn: () => ds.storefrontProducts.list(filters),
  });
}

export function useStorefrontProduct(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...PROD_KEY, id],
    queryFn: () => ds.storefrontProducts.getById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<StorefrontProduct>(qc, PROD_KEY, id),
  });
}

export function useStorefrontProductByArticle(articleId: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...PROD_KEY, 'by-article', articleId],
    queryFn: () => ds.storefrontProducts.getByArticleId(articleId),
    enabled: !!articleId,
  });
}

function readCachedStorefrontProduct(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
): Partial<StorefrontProduct> {
  const direct = qc.getQueryData<StorefrontProduct>([...PROD_KEY, id]);
  if (direct) return direct;
  for (const [, list] of qc.getQueriesData<StorefrontProduct[]>({ queryKey: PROD_KEY })) {
    if (Array.isArray(list)) {
      const found = list.find((p) => p.id === id);
      if (found) return found;
    }
  }
  return { id };
}

export function useCreateStorefrontProduct() {
  const ds = useDataService();
  return useResilientMutation<CreateStorefrontProductInput, StorefrontProduct>({
    entity: 'storefront_products',
    op: 'create',
    invalidate: [PROD_KEY],
    online: (input) => ds.storefrontProducts.create(input),
    // Spread the input and fill optional fields with their resting defaults.
    // The server-confirmed row replaces this on reconnect.
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        articleId: input.articleId,
        slug: input.slug ?? OFFLINE_PREFIX + clientId.slice(0, 6).toLowerCase(),
        nameFr: input.nameFr ?? null,
        nameAr: input.nameAr ?? null,
        nameEn: input.nameEn ?? null,
        descriptionFr: input.descriptionFr ?? null,
        descriptionAr: input.descriptionAr ?? null,
        descriptionEn: input.descriptionEn ?? null,
        heroImageUrl: input.heroImageUrl ?? null,
        galleryImages: input.galleryImages ?? [],
        priceOverride: input.priceOverride ?? null,
        compareAtPrice: input.compareAtPrice ?? null,
        seoTitleFr: input.seoTitleFr ?? null,
        seoTitleAr: input.seoTitleAr ?? null,
        seoTitleEn: input.seoTitleEn ?? null,
        seoDescriptionFr: input.seoDescriptionFr ?? null,
        seoDescriptionAr: input.seoDescriptionAr ?? null,
        seoDescriptionEn: input.seoDescriptionEn ?? null,
        adsLinkSlug: input.adsLinkSlug ?? null,
        createdAt: nowIso(),
        updatedAt: nowIso(),
      }) as StorefrontProduct,
    applyOptimistic: (qc, newProd) => {
      qc.setQueriesData<StorefrontProduct[]>({ queryKey: PROD_KEY }, (old) => {
        if (!Array.isArray(old)) return old;
        if (old.some((p) => p.id === newProd.id)) return old;
        return [...old, newProd];
      });
    },
  });
}

export function useUpdateStorefrontProduct() {
  const qc = useQueryClient();
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateStorefrontProductInput }, StorefrontProduct>({
    entity: 'storefront_products',
    op: 'update',
    invalidate: [PROD_KEY],
    online: ({ id, input }) => ds.storefrontProducts.update(id, input),
    optimistic: ({ id, input }) => {
      const cached = readCachedStorefrontProduct(qc, id);
      return { ...cached, ...input, id, updatedAt: nowIso() } as StorefrontProduct;
    },
    applyOptimistic: (qc, updated, { id }) => {
      qc.setQueriesData<StorefrontProduct[]>({ queryKey: PROD_KEY }, (old) =>
        Array.isArray(old) ? old.map((p) => (p.id === id ? updated : p)) : old,
      );
      qc.setQueryData<StorefrontProduct>([...PROD_KEY, id], updated);
    },
  });
}

export function useDeleteStorefrontProduct() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'storefront_products',
    op: 'delete',
    invalidate: [PROD_KEY],
    online: (id) => ds.storefrontProducts.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _result, id) => {
      qc.setQueriesData<StorefrontProduct[]>({ queryKey: PROD_KEY }, (old) =>
        Array.isArray(old) ? old.filter((p) => p.id !== id) : old,
      );
      qc.removeQueries({ queryKey: [...PROD_KEY, id] });
    },
  });
}

// ---- Online customers ----

const OC_KEY = ['online-customers'] as const;

export function useOnlineCustomers(filters?: OnlineCustomerFilters) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...OC_KEY, filters],
    queryFn: () => ds.onlineCustomers.list(filters),
  });
}

export function useOnlineCustomer(id: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...OC_KEY, id],
    queryFn: () => ds.onlineCustomers.getById(id),
    enabled: !!id,
  });
}

export function useCreateOnlineCustomer() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOnlineCustomerInput) => ds.onlineCustomers.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: OC_KEY }),
  });
}

export function useUpdateOnlineCustomer() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateOnlineCustomerInput }) =>
      ds.onlineCustomers.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: OC_KEY }),
  });
}

export function useDeleteOnlineCustomer() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ds.onlineCustomers.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: OC_KEY }),
  });
}

export function useBlockOnlineCustomer() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      ds.onlineCustomers.block(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: OC_KEY }),
  });
}

export function useUnblockOnlineCustomer() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ds.onlineCustomers.unblock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: OC_KEY }),
  });
}

export function useLinkOnlineCustomer() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, customerId }: { id: string; customerId: string }) =>
      ds.onlineCustomers.link(id, customerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: OC_KEY }),
  });
}

// ---- Store settings (singleton) ----

const SETTINGS_KEY = ['store-settings'] as const;

export function useStoreSettings() {
  const ds = useDataService();
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => ds.storeSettings.get(),
  });
}

export function useUpdateStoreSettings() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStoreSettingsInput) => ds.storeSettings.update(input),
    onSuccess: (settings: StoreSettings) => {
      qc.setQueryData(SETTINGS_KEY, settings);
      qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

/** Cheap point-in-time check; debounced caller in the UI. */
export function useIsSubdomainTaken(subdomain: string, excludeOwnId: string | null) {
  const ds = useDataService();
  const v = subdomain.trim().toLowerCase();
  return useQuery({
    queryKey: ['store-settings-subdomain-taken', v, excludeOwnId ?? ''] as const,
    queryFn: () => ds.storeSettings.isSubdomainTaken(v, excludeOwnId),
    enabled: v.length >= 3,
    staleTime: 30_000,
  });
}

export function useIsCustomDomainTaken(host: string, excludeOwnId: string | null) {
  const ds = useDataService();
  const v = host.trim().toLowerCase();
  return useQuery({
    queryKey: ['store-settings-custom-domain-taken', v, excludeOwnId ?? ''] as const,
    queryFn: () => ds.storeSettings.isCustomDomainTaken(v, excludeOwnId),
    enabled: v.length >= 4 && v.includes('.'),
    staleTime: 30_000,
  });
}

/** Tells the platform's Cloudflare Pages project to start serving the
 *  storefront on a given hostname — either a Gesty subdomain (eg
 *  "tunielec.gesty.tn") or a tenant's own custom domain (eg
 *  "www.tunielec.tn"). Without this call, changing the value in
 *  store_settings only updates the database; Cloudflare doesn't know
 *  about the new hostname and visitors get a 404 from the edge.
 *
 *  Reliability:
 *    - The server function retries the Cloudflare API call internally
 *      with exponential backoff on transient errors.
 *    - This client retries up to 3 times with backoff if the server
 *      reports a transient failure (5xx with `transient: true`) — that
 *      covers the case where Cloudflare itself returned 503 for the
 *      whole pages function.
 *    - The server records every attempt to public.domain_sync_jobs;
 *      if everything still fails, the dashboard's
 *      useEnsureSubdomainBound hook reads pending rows on the next
 *      load and replays them silently.
 */
export function useSyncStorefrontDomain() {
  return useMutation({
    retry: (failureCount, err) => {
      // Don't retry permanent failures the user has to fix (auth, ownership,
      // bad input). Only retry "transient" errors flagged by the server.
      if (failureCount >= 3) return false;
      const msg = err instanceof Error ? err.message : String(err);
      const transientCodes = [
        'cloudflare_unreachable',
        'tenant_lookup_failed',
        'unhandled_exception',
        'http_502',
        'http_503',
        'http_504',
        'TypeError', // network blip in fetch()
      ];
      return transientCodes.some((c) => msg.includes(c));
    },
    retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 5_000),
    mutationFn: async (
      input:
        | {
            kind: 'subdomain';
            old: string | null;
            new: string;
            accessToken: string;
          }
        | {
            kind: 'host';
            old: string | null;
            new: string;
            accessToken: string;
          },
    ): Promise<{ added: string; removed: string | null }> => {
      if (!input.accessToken) throw new Error('not_authenticated');
      const body =
        input.kind === 'host'
          ? { newHost: input.new, oldHost: input.old }
          : { new: input.new, old: input.old };
      const res = await fetch('/api/storefront/sync-domain', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${input.accessToken}`,
          'X-Supabase-URL': import.meta.env.VITE_SUPABASE_URL,
          'X-Supabase-AnonKey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload.success) {
        const code = payload.error ?? `http_${res.status}`;
        const extra = payload.detail ?? payload.message ?? '';
        const msg = extra
          ? `${code}: ${typeof extra === 'string' ? extra : JSON.stringify(extra)}`
          : code;
        throw new Error(typeof msg === 'string' ? msg : 'sync_failed');
      }
      return { added: payload.added, removed: payload.removed ?? null };
    },
  });
}

// ---- Store theme ----

const THEME_KEY = ['store-theme'] as const;

export function useStoreTheme() {
  const ds = useDataService();
  return useQuery({
    queryKey: THEME_KEY,
    queryFn: () => ds.storeTheme.get(),
  });
}

export function useUpdateStoreTheme() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStoreThemeInput) => ds.storeTheme.update(input),
    onSuccess: (theme: StoreTheme) => {
      qc.setQueryData(THEME_KEY, theme);
      qc.invalidateQueries({ queryKey: THEME_KEY });
    },
  });
}

// ---- Store banners ----

const BANNERS_KEY = ['store-banners'] as const;

export function useStoreBanners() {
  const ds = useDataService();
  return useQuery({
    queryKey: BANNERS_KEY,
    queryFn: () => ds.storeBanners.list(),
  });
}

function readCachedStoreBanner(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
): Partial<StoreBanner> {
  for (const [, list] of qc.getQueriesData<StoreBanner[]>({ queryKey: BANNERS_KEY })) {
    if (Array.isArray(list)) {
      const found = list.find((b) => b.id === id);
      if (found) return found;
    }
  }
  return { id };
}

export function useCreateStoreBanner() {
  const ds = useDataService();
  return useResilientMutation<CreateStoreBannerInput, StoreBanner>({
    entity: 'store_banners',
    op: 'create',
    invalidate: [BANNERS_KEY],
    online: (input) => ds.storeBanners.create(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        slot: input.slot,
        displayOrder: input.displayOrder ?? 0,
        desktopImageUrl: input.desktopImageUrl ?? null,
        mobileImageUrl: input.mobileImageUrl ?? null,
        titleFr: input.titleFr ?? null,
        titleAr: input.titleAr ?? null,
        titleEn: input.titleEn ?? null,
        descriptionFr: input.descriptionFr ?? null,
        descriptionAr: input.descriptionAr ?? null,
        descriptionEn: input.descriptionEn ?? null,
        ctaTextFr: input.ctaTextFr ?? null,
        ctaTextAr: input.ctaTextAr ?? null,
        ctaTextEn: input.ctaTextEn ?? null,
        ctaLink: input.ctaLink ?? null,
        ctaPosition: input.ctaPosition ?? 'left',
        textAlignment: input.textAlignment ?? 'left',
        overlayOpacity: input.overlayOpacity ?? 0,
        isVisible: input.isVisible ?? true,
        createdAt: nowIso(),
        updatedAt: nowIso(),
      }) as StoreBanner,
    applyOptimistic: (qc, newBanner) => {
      qc.setQueryData<StoreBanner[]>(BANNERS_KEY, (old) => {
        if (!Array.isArray(old)) return [newBanner];
        if (old.some((b) => b.id === newBanner.id)) return old;
        return [...old, newBanner].sort((a, b) => a.displayOrder - b.displayOrder);
      });
    },
  });
}

export function useUpdateStoreBanner() {
  const qc = useQueryClient();
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateStoreBannerInput }, StoreBanner>({
    entity: 'store_banners',
    op: 'update',
    invalidate: [BANNERS_KEY],
    online: ({ id, input }) => ds.storeBanners.update(id, input),
    optimistic: ({ id, input }) => {
      const cached = readCachedStoreBanner(qc, id);
      return { ...cached, ...input, id, updatedAt: nowIso() } as StoreBanner;
    },
    applyOptimistic: (qc, updated, { id }) => {
      qc.setQueryData<StoreBanner[]>(BANNERS_KEY, (old) =>
        Array.isArray(old) ? old.map((b) => (b.id === id ? updated : b)) : old,
      );
    },
  });
}

export function useDeleteStoreBanner() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'store_banners',
    op: 'delete',
    invalidate: [BANNERS_KEY],
    online: (id) => ds.storeBanners.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _result, id) => {
      qc.setQueryData<StoreBanner[]>(BANNERS_KEY, (old) =>
        Array.isArray(old) ? old.filter((b) => b.id !== id) : old,
      );
    },
  });
}

// ---- Store pages ----

const PAGES_KEY = ['store-pages'] as const;

export function useStorePages() {
  const ds = useDataService();
  return useQuery({
    queryKey: PAGES_KEY,
    queryFn: () => ds.storePages.list(),
  });
}

export function useStorePage(id: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...PAGES_KEY, id],
    queryFn: () => ds.storePages.getById(id),
    enabled: !!id,
  });
}

function readCachedStorePage(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
): Partial<StorePage> {
  const direct = qc.getQueryData<StorePage>([...PAGES_KEY, id]);
  if (direct) return direct;
  for (const [, list] of qc.getQueriesData<StorePage[]>({ queryKey: PAGES_KEY })) {
    if (Array.isArray(list)) {
      const found = list.find((p) => p.id === id);
      if (found) return found;
    }
  }
  return { id };
}

export function useUpdateStorePage() {
  const qc = useQueryClient();
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateStorePageInput }, StorePage>({
    entity: 'store_pages',
    op: 'update',
    invalidate: [PAGES_KEY],
    online: ({ id, input }) => ds.storePages.update(id, input),
    optimistic: ({ id, input }) => {
      const cached = readCachedStorePage(qc, id);
      return { ...cached, ...input, id, updatedAt: nowIso() } as StorePage;
    },
    applyOptimistic: (qc, updated, { id }) => {
      qc.setQueriesData<StorePage[]>({ queryKey: PAGES_KEY }, (old) =>
        Array.isArray(old) ? old.map((p) => (p.id === id ? updated : p)) : old,
      );
      qc.setQueryData<StorePage>([...PAGES_KEY, id], updated);
    },
  });
}

// ---- Checkout fields ----

const CHECKOUT_KEY = ['store-checkout-fields'] as const;

export function useCheckoutFields() {
  const ds = useDataService();
  return useQuery({
    queryKey: CHECKOUT_KEY,
    queryFn: () => ds.storeCheckoutFields.list(),
  });
}

function readCachedCheckoutField(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
): Partial<StoreCheckoutField> {
  for (const [, list] of qc.getQueriesData<StoreCheckoutField[]>({ queryKey: CHECKOUT_KEY })) {
    if (Array.isArray(list)) {
      const found = list.find((f) => f.id === id);
      if (found) return found;
    }
  }
  return { id };
}

export function useCreateCheckoutField() {
  const ds = useDataService();
  return useResilientMutation<CreateStoreCheckoutFieldInput, StoreCheckoutField>({
    entity: 'store_checkout_fields',
    op: 'create',
    invalidate: [CHECKOUT_KEY],
    online: (input) => ds.storeCheckoutFields.create(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        fieldKey: input.fieldKey,
        fieldType: input.fieldType,
        labelFr: input.labelFr,
        labelAr: input.labelAr ?? null,
        labelEn: input.labelEn ?? null,
        placeholderFr: input.placeholderFr ?? null,
        placeholderAr: input.placeholderAr ?? null,
        placeholderEn: input.placeholderEn ?? null,
        options: input.options ?? null,
        isRequired: input.isRequired ?? false,
        isVisible: input.isVisible ?? true,
        isDefault: false,
        displayOrder: input.displayOrder ?? 0,
        mapsTo: input.mapsTo ?? null,
        createdAt: nowIso(),
        updatedAt: nowIso(),
      }) as StoreCheckoutField,
    applyOptimistic: (qc, newField) => {
      qc.setQueryData<StoreCheckoutField[]>(CHECKOUT_KEY, (old) => {
        if (!Array.isArray(old)) return [newField];
        if (old.some((f) => f.id === newField.id)) return old;
        return [...old, newField].sort((a, b) => a.displayOrder - b.displayOrder);
      });
    },
  });
}

export function useUpdateCheckoutField() {
  const qc = useQueryClient();
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateStoreCheckoutFieldInput }, StoreCheckoutField>({
    entity: 'store_checkout_fields',
    op: 'update',
    invalidate: [CHECKOUT_KEY],
    online: ({ id, input }) => ds.storeCheckoutFields.update(id, input),
    optimistic: ({ id, input }) => {
      const cached = readCachedCheckoutField(qc, id);
      return { ...cached, ...input, id, updatedAt: nowIso() } as StoreCheckoutField;
    },
    applyOptimistic: (qc, updated, { id }) => {
      qc.setQueryData<StoreCheckoutField[]>(CHECKOUT_KEY, (old) =>
        Array.isArray(old) ? old.map((f) => (f.id === id ? updated : f)) : old,
      );
    },
  });
}

export function useDeleteCheckoutField() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'store_checkout_fields',
    op: 'delete',
    invalidate: [CHECKOUT_KEY],
    online: (id) => ds.storeCheckoutFields.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _result, id) => {
      qc.setQueryData<StoreCheckoutField[]>(CHECKOUT_KEY, (old) =>
        Array.isArray(old) ? old.filter((f) => f.id !== id) : old,
      );
    },
  });
}

// ---- Shipments ----

const SHIPMENTS_KEY = ['shipments'] as const;

export function useShipmentsForOrder(salesDocumentId: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...SHIPMENTS_KEY, 'order', salesDocumentId],
    queryFn: () => ds.shipments.listBySalesDocumentId(salesDocumentId),
    enabled: !!salesDocumentId,
  });
}

export function useCreateShipment() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateShipmentInput) => ds.shipments.create(input),
    onSuccess: (shipment: Shipment) => {
      qc.invalidateQueries({ queryKey: [...SHIPMENTS_KEY, 'order', shipment.salesDocumentId] });
    },
  });
}

export function useUpdateShipment() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateShipmentInput }) =>
      ds.shipments.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: SHIPMENTS_KEY }),
  });
}

export function useDeleteShipment() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ds.shipments.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: SHIPMENTS_KEY }),
  });
}

// ---- Analytics ----

export function useStorefrontAnalytics(range: StorefrontAnalyticsRange) {
  const ds = useDataService();
  return useQuery({
    queryKey: ['storefront-analytics', range],
    queryFn: () => ds.storefrontAnalytics.getAnalytics(range),
    enabled: !!range.from && !!range.to,
  });
}

// ---- Order attribution ----

export function useOrderAttribution(salesDocumentId: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: ['order-attribution', salesDocumentId],
    queryFn: () => ds.orderAttribution.getBySalesDocumentId(salesDocumentId),
    enabled: !!salesDocumentId,
  });
}

// ---- Admin notifications ----

const NOTIF_KEY = ['admin-notifications'] as const;

type NotifListParams = {
  limit?: number;
  isRead?: boolean;
  kind?: NotificationKind;
  kinds?: NotificationKind[];
};

export function useAdminNotifications(params?: NotifListParams) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...NOTIF_KEY, params],
    queryFn: () => ds.adminNotifications.list(params),
    refetchInterval: 8_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useUnreadNotificationCount(filters?: { kind?: NotificationKind; kinds?: NotificationKind[] }) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...NOTIF_KEY, 'unread-count', filters],
    queryFn: () => ds.adminNotifications.getUnreadCount(filters),
    refetchInterval: 8_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/** Scans checks/drafts for upcoming due dates and creates any missing
 *  D-7 / D-3 / D-2 / D-1 / D-0 reminders. Polled every 10 min. The SQL
 *  dedups by metadata.dedupKey so repeated calls are safe. */
export function useEnsureCheckDraftReminders() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...NOTIF_KEY, 'ensure-check-drafts'],
    queryFn: async () => {
      const n = await ds.adminNotifications.ensureCheckDraftReminders();
      if (n > 0) qc.invalidateQueries({ queryKey: NOTIF_KEY });
      return n;
    },
    refetchInterval: 10 * 60_000,
    refetchIntervalInBackground: false,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Backfills low-stock alerts for already-low products. Stock mutations create
 *  alerts immediately; this periodic scan covers older rows and offline sync. */
export function useEnsureLowStockNotifications() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...NOTIF_KEY, 'ensure-low-stock'],
    queryFn: async () => {
      const n = await ds.adminNotifications.ensureLowStockNotifications();
      if (n > 0) qc.invalidateQueries({ queryKey: NOTIF_KEY });
      return n;
    },
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    staleTime: 60_000,
    retry: false,
  });
}

export function useMarkNotificationRead() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ds.adminNotifications.markAsRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });
}

export function useMarkAllNotificationsRead() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => ds.adminNotifications.markAllAsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });
}

export function useDeleteNotification() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ds.adminNotifications.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });
}

export function useDeleteAllReadNotifications() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => ds.adminNotifications.deleteAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });
}

// ---- Abandoned carts ----

const CART_KEY = ['abandoned-carts'] as const;

export function useAbandonedCarts(filters?: { status?: 'active' | 'recovered' | 'contacted' | 'dismissed'; search?: string }) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...CART_KEY, filters],
    queryFn: () => ds.abandonedCarts.list(filters),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useAbandonedCart(id: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...CART_KEY, id],
    queryFn: () => ds.abandonedCarts.getById(id),
    enabled: !!id,
  });
}

export function useUpdateAbandonedCart() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: import('@erp/data-access/interfaces').UpdateAbandonedCartInput }) =>
      ds.abandonedCarts.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: CART_KEY }),
  });
}

export function useDeleteAbandonedCart() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ds.abandonedCarts.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: CART_KEY }),
  });
}

export type {
  StorefrontCategory,
  StorefrontProduct,
  OnlineCustomer,
  StoreSettings,
  StoreTheme,
  StoreBanner,
  StorePage,
  StoreCheckoutField,
};
